<template>
  <div v-show="state" class="page lightmap">
    <navigation-header />
    <div class="main">
      <div v-if="showEyebrow" class="eyebrow-heading">
        {{ $t(`login.${state}.eyebrow`) }}
      </div>

      <div>
        <product-header :heading="heading" :show-logo="!showEyebrow" />
      </div>
      <div v-if="showParagraph" class="paragraph-content">
        {{ paragraph }}
      </div>

      <authenticator
        ref="authComponent" class="authenticator" :class="{ 'no-margin': !showParagraph }"
        @state-changed="onAuthStateChanged"
      />

      <Loading v-if="isLoadingState" />

      <product-benefits v-if="showProductDetails" />
      <product-links v-if="showProductDetails" />
    </div>
    <accounts-footer />
  </div>
</template>

<script setup>
import { watch, computed, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  AuthState, useAuthentication, useProduct, Authenticator,
  VerticalStack, HorizontalStack, Loading, useAnalytics
} from 'ihunter-pathfinder-components';
import NavigationHeader from '../components/NavigationHeader.vue';
import ProductHeader from '../components/ProductHeader.vue';
import ProductBenefits from '../components/ProductBenefits.vue';
import ProductLinks from '../components/ProductLinks.vue';
import AccountsFooter from '../components/AccountsFooter.vue';
import { useRouteManager } from '../composables/useRouteManager';
import { useTestState } from '../composables/useTestState';
import { useActionRouteHandler } from '../composables/useActionRouteHandler';

const { t, te } = useI18n();
const route = useRoute();

const { user, signInWithExistingSession } = useAuthentication();
const { product } = useProduct();
const { logScreenView } = useAnalytics();
const { redirectToHomeOrContinue, getTempQueryParams } = useRouteManager();
const { initializeTestStateFromUrl, TestStateUser, testState } = useTestState();

const state = ref(null);

const authComponent = ref(null);

const isLoadingState = computed(() => {
  return state.value === AuthState.ValidatingSignInLink;
});

const showEyebrow = computed(() => {
  return te(`login.${state.value}.eyebrow`);
});

const heading = computed(() => {
  const key = `login.${state.value}.heading`;
  return te(key) ? t(key) : '';
});

const showParagraph = computed(() => {
  return te(`login.${state.value}.paragraph`);
});

const paragraph = computed(() => {
  return t(`login.${state.value}.paragraph`);
});

const showProductDetails = computed(() => {
  return state.value === AuthState.SignIn && product.value.name === 'accounts';
});

onMounted(async () => {
  console.log('Initialize LoginPage');
  initializeTestStateFromUrl(AuthState);
  if (testState.value) {
    authComponent.value.setAuthState(testState.value);
    if (testState.value !== AuthState.SignInWithLink) {
      authComponent.value.setAuthEmail(TestStateUser.email);
    }
  } else if (!setAuthStateFromQuery()) {
    authComponent.value.checkSignInLink();
  } 
});

watch(user, async () => {
  if (testState.value) {
    user.value = (state.value === AuthState.SignOut) ? TestStateUser : null;
  } else {
    await redirectToHomeOrContinue();
  }
});

watch(route, (newRoute, oldRoute) => {
  setAuthStateFromQuery();
});

const setAuthStateFromQuery = () => {
  const params = getTempQueryParams(['authState', 'authEmail']);
  if (params.authEmail) {
    authComponent.value.setAuthEmail(params.authEmail);
  }
  if (params.authState) {
    authComponent.value.setAuthState(params.authState);
    return true;
  }
  return false;
}

const onAuthStateChanged = async (authState) => {
  console.log(`onAuthStateChanged: ${authState}`);
  if (user.value && authState === AuthState.SignIn) {
    await redirectToHomeOrContinue();
  } else {
    logScreenView(authState);
    state.value = authState;
  }
};
</script>

<style lang="scss" scoped>
@use 'ihunter-pathfinder/styles' as Pathfinder;
@use '../styles/Accounts' as Accounts;

.page {
  @include Accounts.Default;
  @include Accounts.Container;

  .main .no-margin {
    margin-top: 0;
  }
}
</style>
